import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const TESTIMONIALS = [
  {
    name: 'Brandon & Jessie M.',
    neighborhood: 'Meridian-Kessler, Indianapolis',
    text: 'I work at Eli Lilly. HR gives us 2x salary in group life — sounds like a lot until you realize it doesn\'t cover our mortgage or replace my income for more than a year. Benjamin showed us the gap and fixed it for $39/month. Wish we\'d done this on day one.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Nicole K.',
    neighborhood: 'Geist, Indianapolis',
    text: 'I\'ve worked at IU Health for 11 years and assumed my hospital plan was enough. It wasn\'t. Benjamin compared 20 carriers in one call and got me $750,000 of portable coverage for $31/month. Now it follows me no matter where I work.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Tom & Rachel P.',
    neighborhood: 'Broad Ripple, Indianapolis',
    text: 'We bought our first home near the canal and realized we had nothing protecting the mortgage. Benjamin had us covered in 48 hours — no exam, no hard sell, just a quick call and a policy that fits our budget perfectly.',
    stars: 5,
    product: 'Mortgage Protection',
  },
  {
    name: 'Devin S.',
    neighborhood: 'Castleton, Indianapolis',
    text: 'I needed $500,000 in coverage fast and didn\'t want to wait weeks for a medical exam. Benjamin got me approved same-day with no exam. The rate was way lower than I expected. Done in one afternoon.',
    stars: 5,
    product: 'No Medical Exam',
  },
];

const FAQS = [
  {
    q: 'How much does life insurance cost for an Indianapolis family?',
    a: 'Most healthy Indianapolis adults ages 30–45 pay $22–$50/month for $500,000 in 20-year term coverage. A 35-year-old in good health typically pays around $30/month. Because Indianapolis families often carry $250,000–$400,000 mortgages, we usually recommend $750K–$1.5M in total coverage — but it still runs less per month than most cable bills.',
  },
  {
    q: 'I work at Eli Lilly, IU Health, or Salesforce. Isn\'t my group life insurance enough?',
    a: 'Almost never. Group coverage at most Indianapolis employers covers 1–2x your salary — often capped at $50,000–$100,000. Your Indianapolis mortgage alone is probably $275,000+. Worse, group coverage disappears completely the day you change jobs. A private policy is yours forever, regardless of where you work.',
  },
  {
    q: 'How fast can I get life insurance coverage in Indianapolis?',
    a: 'Many carriers offer same-day or next-day approval for healthy applicants. No-exam policies can be active in 24–48 hours. Traditional policies take 2–4 weeks. We\'ll match you to the fastest path based on your health and the coverage amount you need.',
  },
  {
    q: 'Do I need to come to an office, or can we do this remotely?',
    a: 'Completely remote — phone, Zoom, or Google Meet. Most Indianapolis clients complete everything in a single 20-minute call during a lunch break or after the kids are in bed. No office visit needed, ever.',
  },
  {
    q: 'I just bought a home in Indianapolis — what coverage do I need?',
    a: 'At minimum, enough to pay off your mortgage if you pass away. On top of that, income replacement for your spouse — roughly 10x your annual salary. For an Indianapolis family with a $300,000 home and $75,000 income, we typically recommend $750K–$1M in coverage. The monthly cost usually surprises people in a good way.',
  },
  {
    q: 'What carriers do you work with for Indianapolis residents?',
    a: 'We\'re 100% independent and compare 20+ A-rated carriers including Mutual of Omaha, Protective Life, North American Company, Transamerica, Foresters Financial, and more. We shop your profile across all of them and bring you the best rate — not the best commission for us.',
  },
];

const NEIGHBORHOODS = [
  'Broad Ripple', 'Meridian-Kessler', 'Geist', 'Castleton', 'Nora', 'Butler-Tarkington',
  'Fountain Square', 'Mass Ave', 'Irvington', 'Speedway', 'Beech Grove', 'Lawrence',
  'Eagle Creek', 'Greenwood (south side)', 'Zionsville (north side)', 'Avon',
];

const SERVICES = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'Maximum coverage for the lowest cost. Most Indianapolis families ages 25–50 choose a 20 or 30-year term to cover their mortgage and income.',
    cta: 'See Term Life Rates',
    link: '/term-life',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'Indianapolis homes average $280,000+. This policy pays your mortgage completely if you pass away — no foreclosure, ever.',
    cta: 'Protect My Home',
    link: '/mortgage-protection',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Whole Life Insurance',
    desc: 'Builds real cash value year after year. Popular with Indianapolis professionals who\'ve maxed their 401(k) and want a tax-advantaged alternative.',
    cta: 'Explore Whole Life',
    link: '/whole-life',
  },
  {
    icon: 'ri-shield-user-fill',
    title: 'No Medical Exam',
    desc: 'Get approved in 48 hours with zero needles. Perfect for busy Indianapolis parents and professionals who don\'t have time for a full medical exam.',
    cta: 'Fast Approval',
    link: '/no-medical-exam',
  },
];

export default function LifeInsuranceIndianapolisPage() {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', zip: '', insuranceType: '', bestTime: '' });
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Life Insurance Indianapolis IN | Compare 20+ Carriers — Hoosier Life Insurance';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', 'Indianapolis, IN life insurance — independent agent compares 20+ carriers to find your lowest rate. Eli Lilly, IU Health, Salesforce employees: your group plan isn\'t enough. Free 15-min call. (317) 648-2983.');
    setMeta('keywords', 'life insurance Indianapolis Indiana, Indianapolis IN life insurance agent, term life insurance Indianapolis, mortgage protection Indianapolis, Eli Lilly life insurance, IU Health life insurance, Marion County life insurance');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Indianapolis, Indiana');
    setMeta('geo.position', '39.7684;-86.1581');
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://hoosierlifeinsurance.com/life-insurance-indianapolis';

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-indianapolis#faq',
      mainEntity: FAQS.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };

    const localBizSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-indianapolis#org',
      name: 'Hoosier Life Insurance — Indianapolis',
      url: 'https://hoosierlifeinsurance.com/life-insurance-indianapolis',
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Indianapolis',
        addressLocality: 'Indianapolis',
        addressRegion: 'IN',
        postalCode: '46235',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 39.7684,
        longitude: -86.1581,
      },
      areaServed: {
        '@type': 'City',
        name: 'Indianapolis',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '142', bestRating: '5', worstRating: '1' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      description: 'Independent life insurance agency serving Indianapolis, Indiana. Eli Lilly, IU Health, Salesforce employees, and all Marion County families.',
      priceRange: '$$',
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-indianapolis#speakable',
      speakable: { '@type': 'SpeakableSpecification', xpath: ['/html/body//*[@id="indianapolis-price-summary"]'] },
      url: 'https://hoosierlifeinsurance.com/life-insurance-indianapolis',
    };

    [faqSchema, localBizSchema, speakableSchema].forEach((schema, idx) => {
      const id = `schema-indy-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id; el.type = 'application/ld+json';
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => { [0,1,2].forEach(i => document.getElementById(`schema-indy-${i}`)?.remove()); };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append('sms_consent', smsConsent ? 'Yes' : 'No');
      body.append('source_page', 'Indianapolis Life Insurance Landing Page');
      const res = await fetch('https://readdy.ai/api/form/d74mo3f5hic0eqh318ng', {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setSubmitted(true); } else { setError('Something went wrong. Please call us at (317) 648-2983.'); }
    } catch { setError('Something went wrong. Please call us at (317) 648-2983.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <Header />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[140px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/8 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F0A070] text-[13px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">Indianapolis, Indiana — Marion County</span>
              </div>
              <h1 className="text-[36px] sm:text-[46px] md:text-[54px] font-bold text-white leading-[1.1] mb-4">
                Indianapolis Families<br />
                <span className="text-[#D97642]">Overpay Because</span><br />
                They Don&apos;t Compare.
              </h1>
              <p className="text-[17px] text-white/80 leading-[1.75] mb-3 max-w-[520px]">
                We compare 20+ carriers at once — free, no pressure, in 15 minutes. Most Indianapolis families find a better rate the first time they actually shop.
              </p>
              <p className="text-[15px] text-white/60 leading-[1.7] mb-8 max-w-[480px]">
                I&apos;m Benjamin Wright — an independent agent in Central Indiana. I don&apos;t work for any carrier. I work for you.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-building-2-line', text: '20+ Carriers Compared' },
                  { icon: 'ri-time-line', text: '15-Minute Free Call' },
                  { icon: 'ri-lock-line', text: 'No Pressure, Ever' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#D97642] text-[14px]`}></i></div>
                    <span className="text-white/90 text-[13px] font-medium">{b.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#indianapolis-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-search-line text-[18px]"></i>
                  See My Indianapolis Rate
                </a>
                <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill text-[18px]"></i>
                  (317) 648-2983
                </a>
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { value: '880K+', label: 'Indianapolis Residents', sub: 'Indiana\'s largest city' },
                  { value: '$61K', label: 'Median Household Income', sub: 'Marion County average' },
                  { value: '$280K+', label: 'Avg Indianapolis Home', sub: 'And growing year over year' },
                  { value: '20+', label: 'Carriers We Compare', sub: 'You get the best rate available' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="text-[26px] font-black text-[#D97642] leading-none mb-1">{s.value}</div>
                    <div className="text-[13px] font-semibold text-white leading-tight">{s.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
              <img
                src="https://public.readdy.ai/ai/img_res/41764510445373e95ea72674af3848ef.jpg"
                alt="Indianapolis Indiana Family Life Insurance"
                className="w-full h-[220px] object-cover object-top rounded-2xl"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width={600}
                height={380}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Proof Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex">{[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[18px]"></i>)}</div>
            <span className="text-[15px] font-bold text-[#1A3A52]">4.9 / 5</span>
            <span className="text-[13px] text-[#718096]">from 142 Indiana families</span>
          </div>
          <div className="flex items-center gap-6">
            {[['500+','Families Protected'],['$0','Cost to You'],['15 min','Average Call Time']].map(([v, l]) => (
              <div key={l} className="text-center">
                <div className="text-[22px] font-black text-[#D97642]">{v}</div>
                <div className="text-[11px] text-[#718096] uppercase tracking-wide">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-[#4A5568]">
            <i className="ri-shield-check-fill text-[#D97642] text-[16px]"></i>
            <span>Indiana Licensed · <strong>Not a Call Center</strong></span>
          </div>
        </div>
      </div>

      {/* ── Problem / Solution ── */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 mb-4">
                <i className="ri-error-warning-line text-red-500 text-[14px]"></i>
                <span className="text-red-600 text-[12px] font-semibold uppercase tracking-wide">The Problem Most Indianapolis Families Don&apos;t Know About</span>
              </div>
              <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] leading-[1.2] mb-5">
                Your Eli Lilly or IU Health insurance disappears the day you change jobs.
              </h2>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-4">
                Most Indianapolis professionals have life insurance through work. It feels like enough. Until it isn&apos;t.
              </p>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-6">
                Group plans typically only cover 1–2x your salary. Your Indianapolis mortgage is probably $250,000+. Your family needs 10x your income — and group coverage isn&apos;t portable.
              </p>
              <div className="space-y-3">
                {[
                  'Group coverage = 1–2x salary (almost never enough)',
                  'You lose it the moment you leave your employer',
                  'Rates increase every year you delay getting your own policy',
                  'A health change can disqualify you from future coverage',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-red-100 rounded-full mt-0.5 flex-shrink-0">
                      <i className="ri-close-line text-red-500 text-[12px]"></i>
                    </div>
                    <span className="text-[14px] text-[#4A5568]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-3 py-1 mb-5">
                <i className="ri-check-double-line text-[#D97642] text-[14px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-wide">The Fix — Takes 15 Minutes</span>
              </div>
              <h3 className="text-[26px] font-bold text-white leading-tight mb-4">What a private policy does for your Indianapolis family:</h3>
              {[
                { icon: 'ri-home-heart-line', title: 'Pays off your Indianapolis home completely', desc: 'Your family stays in their home no matter what.' },
                { icon: 'ri-team-line', title: 'Replaces your income for 10–20 years', desc: 'Your spouse doesn\'t have to figure it out alone.' },
                { icon: 'ri-graduation-cap-line', title: 'Funds your kids\' college', desc: 'Their future stays on track, even if yours changes.' },
                { icon: 'ri-lock-line', title: 'Follows you from job to job, forever', desc: 'Eli Lilly, IU Health, startup — doesn\'t matter. It\'s yours.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-[18px]`}></i>
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{item.title}</div>
                    <div className="text-[13px] text-white/60">{item.desc}</div>
                  </div>
                </div>
              ))}
              <a href="#indianapolis-quote-form" className="block text-center mt-6 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer">
                Get My Indianapolis Rate — Free
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Employer Callout Strip ── */}
      <section className="py-10 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-5">
            We help employees from Indianapolis&apos; largest employers close the coverage gap
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { icon: 'ri-medicine-bottle-line', name: 'Eli Lilly and Company' },
              { icon: 'ri-hospital-line', name: 'IU Health' },
              { icon: 'ri-cloud-line', name: 'Salesforce' },
              { icon: 'ri-heart-pulse-line', name: 'Anthem Blue Cross' },
              { icon: 'ri-microscope-line', name: 'Roche Diagnostics' },
              { icon: 'ri-building-line', name: 'Simon Property Group' },
              { icon: 'ri-bank-line', name: 'OneAmerica Financial' },
              { icon: 'ri-government-line', name: 'State of Indiana' },
              { icon: 'ri-book-open-line', name: 'IPS (Public Schools)' },
              { icon: 'ri-stethoscope-line', name: 'Ascension St. Vincent' },
            ].map(e => (
              <div key={e.name} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-xl text-[13px] font-semibold text-[#1A3A52]">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${e.icon} text-[#D97642] text-[15px]`}></i></div>
                {e.name}
              </div>
            ))}
          </div>
          <p className="text-center text-[13px] text-[#718096] mt-4">
            Work somewhere else? We cover <strong>all</strong> Indianapolis and Marion County residents.{' '}
            <a href="#indianapolis-quote-form" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Get your free rate.</a>
          </p>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="py-16 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-3">Life Insurance for Indianapolis Families</h2>
            <p className="text-[16px] text-[#718096] max-w-[600px] mx-auto">
              From Broad Ripple to Castleton — we help Indianapolis families find the right coverage at the right price.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(svc => (
              <a key={svc.title} href={svc.link} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:bg-white transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${svc.icon} text-white text-[22px]`}></i>
                </div>
                <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors">{svc.title}</h3>
                <p className="text-[13px] text-[#4A5568] leading-[1.7] mb-4">{svc.desc}</p>
                <div className="flex items-center gap-1 text-[13px] text-[#D97642] font-semibold">
                  <span>{svc.cta}</span>
                  <i className="ri-arrow-right-line text-[14px]"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Table ── */}
      <section id="indianapolis-price-summary" className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in Indianapolis?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">
              Rates below are for healthy, non-smoking Indianapolis residents. Indiana rates run 8–12% below the national average. These are actual market rates from the 20+ carriers we compare.
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  <th className="px-5 py-3.5 text-left font-semibold">Age</th>
                  <th className="px-5 py-3.5 text-left font-semibold">Coverage</th>
                  <th className="px-5 py-3.5 text-left font-semibold">Term</th>
                  <th className="px-5 py-3.5 text-center font-semibold">Male/Month</th>
                  <th className="px-5 py-3.5 text-center font-semibold">Female/Month</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', coverage: '$500,000', term: '20-year', male: '$27', female: '$22' },
                  { age: '35', coverage: '$500,000', term: '20-year', male: '$31', female: '$26' },
                  { age: '35', coverage: '$1,000,000', term: '20-year', male: '$50', female: '$42' },
                  { age: '40', coverage: '$500,000', term: '20-year', male: '$44', female: '$37' },
                  { age: '40', coverage: '$750,000', term: '20-year', male: '$62', female: '$52' },
                  { age: '45', coverage: '$500,000', term: '20-year', male: '$67', female: '$55' },
                  { age: '50', coverage: '$250,000', term: '20-year', male: '$70', female: '$58' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.coverage}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 text-center font-bold text-[#D97642]">{row.male}/mo</td>
                    <td className="px-5 py-3 text-center font-bold text-[#D97642]">{row.female}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and individual health profile.</p>
            <a href="#indianapolis-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[16px]"></i>
              Get My Exact Indianapolis Rate
            </a>
          </div>
        </div>
      </section>

      {/* ── Form + Testimonials ── */}
      <section id="indianapolis-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-[26px] font-bold text-[#1A3A52] mb-2">What Indianapolis Families Are Saying</h2>
                <p className="text-[14px] text-[#718096]">Real people. Real neighborhoods. No scripts.</p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[14px]"></i>)}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-3 italic">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-[#1A3A52]">{t.name}</div>
                      <div className="text-[12px] text-[#718096]">{t.neighborhood}</div>
                    </div>
                    <span className="text-[11px] bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-1 rounded-full whitespace-nowrap">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-map-pin-line text-[#D97642] text-[13px]"></i>
                    <span className="text-[#D97642] text-[12px] font-semibold">Serving All Indianapolis &amp; Marion County</span>
                  </div>
                  <h2 className="text-[24px] font-bold text-[#1A3A52] mb-1">Get Your Free Indianapolis Quote</h2>
                  <p className="text-[14px] text-[#718096]">Takes 2 minutes. No obligation. We respond within 24 hours.</p>
                </div>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-[32px]"></i>
                    </div>
                    <h3 className="text-[22px] font-bold text-[#1A3A52] mb-2">You&apos;re all set!</h3>
                    <p className="text-[15px] text-[#4A5568] mb-6">We&apos;ll reach out within 24 hours with Indianapolis-specific options.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-[#D97642] text-white font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap text-[14px]">
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="source_page" value="Indianapolis Life Insurance Landing Page" />
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Full Name <span className="text-[#D97642]">*</span></label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-gray-400 text-[15px]"></i></div>
                        <input type="text" name="name" required placeholder="Jane Smith" value={formData.name} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-gray-400 text-[15px]"></i></div>
                          <input type="tel" name="phone" required placeholder="(317) 000-0000" value={formData.phone} onChange={handleChange} className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-gray-400 text-[15px]"></i></div>
                          <input type="email" name="email" required placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Indianapolis ZIP Code <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-gray-400 text-[15px]"></i></div>
                          <input type="text" name="zip" required placeholder="46220" pattern="[0-9]{5}" maxLength={5} value={formData.zip} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">What Are You Looking For?</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-shield-line text-gray-400 text-[15px]"></i></div>
                          <select name="insuranceType" value={formData.insuranceType} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors bg-white appearance-none cursor-pointer">
                            <option value="">Help me decide</option>
                            <option value="term_life">Term Life Insurance</option>
                            <option value="whole_life">Whole Life Insurance</option>
                            <option value="mortgage_protection">Mortgage Protection</option>
                            <option value="final_expense">Final Expense Insurance</option>
                            <option value="annuity">Annuity / Retirement</option>
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none"><i className="ri-arrow-down-s-line text-gray-400 text-[16px]"></i></div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-gray-400 text-[15px]"></i></div>
                        <select name="bestTime" value={formData.bestTime} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors bg-white appearance-none cursor-pointer">
                          <option value="">Any time works</option>
                          <option value="morning">Morning (8am – 12pm)</option>
                          <option value="afternoon">Afternoon (12pm – 5pm)</option>
                          <option value="evening">Evening (5pm – 9pm)</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none"><i className="ri-arrow-down-s-line text-gray-400 text-[16px]"></i></div>
                      </div>
                    </div>
                    {error && <p className="text-[13px] text-red-500 flex items-center gap-1.5"><i className="ri-error-warning-line text-[15px]"></i>{error}</p>}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="sms_consent" required checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer" />
                        <span className="text-[12px] text-gray-600 leading-relaxed">
                          <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance about my quote request. Reply STOP anytime.{' '}
                          <a href="/privacy-policy" className="text-[#D97642] underline">Privacy Policy</a>
                        </span>
                      </label>
                    </div>
                    <button type="submit" disabled={submitting || !smsConsent} className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                      {submitting ? <><i className="ri-loader-4-line animate-spin text-[18px]"></i> Sending...</> : <><i className="ri-send-plane-fill text-[16px]"></i> Show Me My Indianapolis Rates — Free</>}
                    </button>
                    <p className="text-[11px] text-[#718096] text-center">No obligation. We never sell your information.</p>
                  </form>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 bg-[#1A3A52] rounded-xl p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                  <i className="ri-phone-fill text-white text-[20px]"></i>
                </div>
                <div>
                  <div className="text-[12px] text-white/60 uppercase tracking-wider font-semibold">Prefer to call directly?</div>
                  <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-[#D97642] transition-colors cursor-pointer">(317) 648-2983</a>
                  <div className="text-[12px] text-white/60">Mon–Fri 9AM–6PM · Sat 10AM–2PM · Evenings by appointment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Neighborhoods ── */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6 text-center">We Serve All Indianapolis Neighborhoods</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {NEIGHBORHOODS.map(n => (
              <div key={n} className="flex items-center gap-1.5 px-3 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] font-medium text-[#1A3A52]">
                <i className="ri-map-pin-line text-[#D97642] text-[14px]"></i>
                {n}
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#718096] text-center mt-6">
            Serving all Marion County ZIP codes including 46201–46280.{' '}
            <a href="tel:+13176482983" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Call us for any ZIP in Indiana.</a>
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-20 bg-[#F7F9FB]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-3">Indianapolis Life Insurance FAQ</h2>
            <p className="text-[15px] text-[#718096]">The questions Indianapolis families ask us most.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border transition-all ${openFaq === i ? 'border-[#D97642]/50 bg-white' : 'border-gray-200 bg-white hover:border-[#D97642]/30'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer gap-3">
                  <span className="text-[15px] font-semibold text-[#1A3A52] leading-snug">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#D97642] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaq === i ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                {openFaq === i && <div className="px-6 pb-5"><p className="text-[14px] text-[#4A5568] leading-[1.8]">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[32px] md:text-[40px] font-bold text-white mb-4">
            Indianapolis families deserve real coverage, not just a group plan.
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.7] mb-8 max-w-[580px] mx-auto">
            15 minutes. Zero pressure. You&apos;ll know exactly what your family needs — and exactly what it costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#indianapolis-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[18px]"></i>
              Get My Free Rate Now
            </a>
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-[18px]"></i>
              Schedule a Call
            </a>
          </div>
          <p className="text-[13px] text-white/40 mt-6">Serving all Indianapolis and Marion County families</p>
        </div>
      </section>

      {/* ── Cross-Link Cluster ── */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Also Serving Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Fishers', href: '/life-insurance-fishers', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Noblesville', href: '/life-insurance-noblesville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
            ].map(link => (
              <a key={link.label} href={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}